<script>
	import { onMount } from 'svelte';

	let email = '';
	let password = '';
	let error = '';
	let success = '';
	let loading = false;
	let rememberMe = false;

	onMount(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('registered') === 'true') {
			success = 'Account created. Sign in to continue.';
		}
	});

	async function handleLogin(e) {
		e.preventDefault();
		loading = true;
		error = '';

		try {
			const response = await fetch('/api/auth/login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, password })
			});

			if (!response.ok) {
				const data = await response.json();
				throw new Error(data.message || 'Login failed');
			}

			window.location.href = '/dashboard';
		} catch (err) {
			error = err.message;
		} finally {
			loading = false;
		}
	}

	function handleGoogleAuth() {
		window.location.href = '/api/auth/google';
	}
</script>

<svelte:head>
	<title>Sign In | OS Studio</title>
	<meta name="description" content="Sign in to OS Studio and continue building your operating platform." />
</svelte:head>

<section class="relative isolate min-h-screen overflow-hidden bg-gradient-to-br from-[#0b0716] via-[#251046] to-[#110922] text-white">
	<div class="absolute inset-0">
		<div class="absolute left-1/2 top-[-8rem] h-[28rem] w-[52rem] -translate-x-1/2 rounded-full bg-purple-500/35 blur-3xl"></div>
		<div class="absolute right-[-7rem] top-16 h-80 w-80 rounded-full bg-indigo-500/25 blur-3xl"></div>
	</div>
	<div class="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:36px_36px] opacity-20"></div>

	<div class="relative container mx-auto grid min-h-screen max-w-7xl items-center gap-10 px-4 py-12 lg:grid-cols-[1fr_430px]">
		<div class="hidden lg:block">
			<p class="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-purple-200">Welcome Back</p>
			<h1 class="mb-5 text-5xl font-semibold leading-tight">Resume your platform operations in minutes</h1>
			<p class="max-w-xl text-lg leading-relaxed text-purple-100">
				Continue from where you left off with your build pipeline, deployment status, and operational dashboard all in one place.
			</p>
		</div>

		<div class="rounded-3xl border border-white/20 bg-white/10 p-7 shadow-2xl backdrop-blur-xl sm:p-8">
			<div class="mb-7">
				<h2 class="text-3xl font-semibold">Sign In</h2>
				<p class="mt-2 text-sm text-purple-100">Access your OS Studio workspace</p>
			</div>

			{#if success}
				<div class="mb-4 rounded-xl border border-emerald-200/35 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-100">
					{success}
				</div>
			{/if}

			{#if error}
				<div class="mb-4 rounded-xl border border-red-200/35 bg-red-500/15 px-4 py-3 text-sm text-red-100">
					{error}
				</div>
			{/if}

			<form on:submit={handleLogin} class="space-y-4">
				<div>
					<label for="email" class="mb-2 block text-sm font-semibold text-purple-100">Email Address</label>
					<input
						id="email"
						type="email"
						bind:value={email}
						placeholder="you@company.com"
						required
						class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white placeholder:text-purple-200/70 focus:border-purple-300 focus:outline-none"
					/>
				</div>

				<div>
					<label for="password" class="mb-2 block text-sm font-semibold text-purple-100">Password</label>
					<input
						id="password"
						type="password"
						bind:value={password}
						placeholder="••••••••"
						required
						class="w-full rounded-xl border border-white/25 bg-slate-950/70 px-4 py-3 text-white placeholder:text-purple-200/70 focus:border-purple-300 focus:outline-none"
					/>
				</div>

				<div class="flex items-center justify-between">
					<label class="flex items-center gap-2 text-sm text-purple-100">
						<input type="checkbox" bind:checked={rememberMe} class="h-4 w-4 rounded border-white/40 bg-transparent" />
						Remember me
					</label>
					<a href="/forgot-password" class="text-sm text-purple-200 hover:text-white">Forgot password?</a>
				</div>

				<button
					type="submit"
					disabled={loading}
					class="w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white transition-all hover:bg-primary-400 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] disabled:opacity-50"
				>
					{loading ? 'Signing in...' : 'Sign In'}
				</button>
			</form>

			<div class="relative my-6">
				<div class="absolute inset-0 flex items-center">
					<div class="w-full border-t border-white/20" />
				</div>
				<div class="relative flex justify-center text-xs uppercase tracking-[0.14em]">
					<span class="bg-transparent px-2 text-purple-200">or continue with</span>
				</div>
			</div>

			<button
				type="button"
				on:click={handleGoogleAuth}
				class="flex w-full items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-3 font-semibold transition hover:bg-white/20"
			>
				<svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
					<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
					<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
					<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
					<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
				</svg>
				Sign in with Google
			</button>

			<p class="mt-6 text-center text-sm text-purple-100">
				Need an account?
				<a href="/register" class="font-semibold text-purple-200 hover:text-white">Sign up</a>
			</p>
		</div>
	</div>
</section>
